import { GridRowModes, GridToolbarContainer, GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';
import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

import { dataGridPremiumColumns } from '../../../utils/helper';

import {
  DataGridIdentifiers,
  FormErrorUI,
  AutoCompleteSelectUI,
  AddButtonUi,
  DatagridPremiumUi,
  InputUI,
} from '../../Interface';

function EditToolbar(props) {
  const { disabled, push, defTeam } = props;
  const { values, validateField } = useFormikContext();

  const handleClick = async () => {
    const newData = {
      ...defTeam,
      id: uuidv4(),
    };

    await push(newData);

    const fieldsToValidate = [
      `team.${values.team.length ?? 0}.resourceOneId`,
      `team.${values.team.length ?? 0}.position`,
      `team.${values.team.length ?? 0}.specialization`,
    ];

    for (const field of fieldsToValidate) {
      await validateField(field);
    }
  };

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={handleClick} />
    </GridToolbarContainer>
  );
}

const ComplaintReportD1 = ({ disabled, defTeam, autoCompleteOptions, autocompleteData }) => {
  const apiRef = useGridApiRef();
  const { values, validateField, setFieldValue, errors } = useFormikContext();

  const getValueIndex = (values, id) => {
    return values.team.findIndex((rc) => rc.id === id);
  };

  const columns = (setFieldValue, validateField, errors, remove) => {
    return dataGridPremiumColumns([
      {
        field: 'resourceOneId',
        headerName: 'Név',
        type: 'string',
        editable: true,
        renderCell: (data) => {
          const findedResourceOne = autoCompleteOptions.resourceOne?.find(
            (item) => item.value === data.row?.resourceOneId
          );
          return findedResourceOne?.label;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const findedResourceOne = autoCompleteOptions.resourceOne?.find(
            (item) => item.value === data.row.resourceOneId
          );

          return (
            <Field
              name={`team.${valueIndex}.resourceOneId`}
              component={AutoCompleteSelectUI}
              helperText={<FormErrorUI message={errors?.team?.[valueIndex]?.resourceOneId} />}
              selectedValue={values.team?.[valueIndex]?.resourceOneId}
              selectedLabelValue={findedResourceOne?.label}
              onChange={(_e, newVal) => {
                setFieldValue(`team.${valueIndex}.resourceOneId`, newVal?.value ?? null);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newVal?.value ?? null,
                });

                const findedR1 = autocompleteData?.resourceOne?.find((r1) => r1.id === newVal?.value);

                if (findedR1) {
                  setFieldValue(`team.${valueIndex}.position`, findedR1.position);
                  setFieldValue(`team.${valueIndex}.specialization`, findedR1.specialization);
                } else {
                  setFieldValue(`team.${valueIndex}.position`, '');
                  setFieldValue(`team.${valueIndex}.specialization`, '');
                }
              }}
              dataset={autoCompleteOptions.resourceOne}
              onBlur={async () => await validateField(`team.${valueIndex}.resourceOneId`)}
            />
          );
        },
      },
      {
        field: 'position',
        headerName: 'Pozíció',
        type: 'string',
        editable: true,
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return values.team?.[valueIndex]?.position;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`team.${valueIndex}.position`}
              type="text"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.team?.[valueIndex]?.position} />}
              onChange={(e) => {
                setFieldValue(`team.${valueIndex}.position`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`team.${valueIndex}.position`)}
            />
          );
        },
      },
      {
        field: 'specialization',
        headerName: 'Szakterület',
        type: 'string',
        editable: true,
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return values.team?.[valueIndex]?.specialization;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`team.${valueIndex}.specialization`}
              type="text"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.team?.[valueIndex]?.specialization} />}
              onChange={(e) => {
                setFieldValue(`team.${valueIndex}.specialization`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`team.${valueIndex}.specialization`)}
            />
          );
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id }) => {
          return [
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => {
                const valueIndex = getValueIndex(values, id);
                return remove(valueIndex);
              }}
              color="inherit"
              disabled={disabled}
            />,
          ];
        },
      },
    ]);
  };

  return (
    <div className="overflow-x-auto">
      <div className="align-middle">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          <FieldArray name="team">
            {({ remove, push }) => (
              <DatagridPremiumUi
                apiRef={apiRef}
                datagridIdentifier={DataGridIdentifiers.ComplaintReportTeam}
                height={400}
                columns={columns(setFieldValue, validateField, errors, remove)}
                rows={values?.team ?? []}
                slots={{
                  toolbar: EditToolbar,
                }}
                headerFilters={false}
                slotProps={{
                  toolbar: {
                    disabled,
                    push,
                    defTeam,
                  },
                }}
                onCellDoubleClick={(_params, event) => {
                  event.defaultMuiPrevented = true;
                }}
                editMode="row"
                getRowHeight={() => 'auto'}
                onCellClick={(params, event) => {
                  if (disabled) return;
                  const rowMode = apiRef.current.getRowMode(params.row.id);
                  if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                }}
              />
            )}
          </FieldArray>
        </div>
      </div>
    </div>
  );
};

export default ComplaintReportD1;
